// src/components/desk/MeetingCountdown.jsx — the live "how long until this meeting" timer.
//
// ONE implementation, three surfaces (home cockpit card, Meetings feed strip, the
// core meeting detail modal). It ticks in the browser, so it must be cheap and it
// must stop being interesting when the meeting is over — a page with 40 meeting
// cards would otherwise run 40 one-second intervals forever.
//
// Two deliberate rules:
//   • the interval is ADAPTIVE — one second only inside the last/first hour (where
//     seconds are the point), thirty seconds otherwise. A meeting three days out
//     does not need 1 Hz.
//   • a FINISHED meeting (closed / failed) and anything more than 12h in the past
//     render nothing at all. "3 days ago" on a closed deal is noise, and the status
//     badge beside it already says what happened.
import { useEffect, useState } from 'react'
import { t, tLog } from '../../lib/i18n.js'

// THE one place that knows where a meeting keeps its time — now lib/meetingOutcome.js, so
// the "owes an outcome" rule beside it can be tested in node. Re-exported here unchanged;
// every surface that imported it from this file still does.
import { meetingScheduledAt } from '../../lib/meetingOutcome.js'
export { meetingScheduledAt }

const MIN = 60_000, HOUR = 3_600_000, DAY = 86_400_000
// Past this much overdue the countdown is history, not a timer.
const STALE_AFTER = 12 * HOUR
// Must track MEETING_FINISHED (MeetingsFeed.jsx) — 'cancelled' was missing here
// from 0396 until 2026-08-06, so a cancelled meeting kept a dead countdown.
const FINISHED = ['closed', 'failed', 'cancelled']

// "2d 3h" · "3h 12m" · "45:20" — the shortest form that still answers "how long".
// The bare mm:ss clock is for the FUTURE only: it reads as a countdown running down.
// Backwards it reads as a time of day ("20:00 ago"), so overdue minutes carry their
// unit instead.
function humanize(ms, late) {
  if (ms >= DAY) return `${Math.floor(ms / DAY)}${t('meeting_cd_d')} ${Math.floor((ms % DAY) / HOUR)}${t('meeting_cd_h')}`
  if (ms >= HOUR) return `${Math.floor(ms / HOUR)}${t('meeting_cd_h')} ${Math.floor((ms % HOUR) / MIN)}${t('meeting_cd_m')}`
  const s = Math.floor(ms / 1000)
  const m = Math.floor(s / 60), sec = s % 60
  return late ? `${m}${t('meeting_cd_m')}` : `${m}:${String(sec).padStart(2, '0')}`
}

export function MeetingCountdown({ at, status, className = '', showIcon = true }) {
  const target = at ? new Date(at).getTime() : NaN
  const [now, setNow] = useState(() => Date.now())
  const diff = Number.isNaN(target) ? null : target - now
  // Sub-hour on either side of the appointment → seconds matter; otherwise idle.
  const near = diff != null && Math.abs(diff) < HOUR
  useEffect(() => {
    if (Number.isNaN(target)) return
    const id = setInterval(() => setNow(Date.now()), near ? 1000 : 30_000)
    return () => clearInterval(id)
  }, [target, near])

  if (diff == null) return null
  if (FINISHED.includes(status)) return null
  if (diff < -STALE_AFTER) return null

  const abs = Math.abs(diff)
  const late = diff < 0
  const tone = late || abs < 15 * MIN
    ? 'text-red-600 dark:text-red-400'
    : abs < HOUR ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground'
  const label = abs < MIN
    ? t('meeting_cd_now')
    : late ? tLog('meeting_cd_ago', { v: humanize(abs, true) }) : tLog('meeting_cd_in', { v: humanize(abs, false) })

  return (
    <span className={`tabular-nums font-medium ${tone} ${className}`}>
      {showIcon ? '⏳ ' : ''}{label}
    </span>
  )
}
